import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { getUserData } from "../redux/user/reducer";

export const USER_STATUS = {
  IDLE: "IDLE",
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAIL: "FAIL",
};

export const ACCESS_LEVELS = {
  ALL: 1,
  LOGGED: 2,
  BNPP_CLIENT: 3,
};

export const useUser = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);
  const [status, setStatus] = useState(USER_STATUS.IDLE);

  useEffect(() => {
    const token = Cookies.get("user_token");

    // If no token, don't fetch
    if (!token) {
      setStatus(USER_STATUS.IDLE);
      return;
    }

    // If user data exists in store (user has valid id), don't fetch
    if (user && user.id && user.id !== -1) {
      setStatus(USER_STATUS.SUCCESS);
      return;
    }

    // Fetch user data
    setStatus(USER_STATUS.PENDING);
    dispatch(getUserData(token))
      .then(() => {
        setStatus(USER_STATUS.SUCCESS);
      })
      .catch(() => {
        setStatus(USER_STATUS.FAIL);
      });
  }, [dispatch, user]);

  // Helper computed values
  const isLoggedIn = user && user.id && user.id !== -1;
  const isBnppClient = isLoggedIn && [3, 5].includes(user.consumer_status);
  const isExpert = isLoggedIn && user.is_expert === true;
  const isEmailConfirmed = isLoggedIn && user.is_email_confirmed === true;
  const hasToken = !!Cookies.get("user_token");

  // Access level helpers
  const hasAccessLevel = level => {
    if (level === ACCESS_LEVELS.ALL) return true;
    if (level === ACCESS_LEVELS.LOGGED) return isLoggedIn;
    if (level === ACCESS_LEVELS.BNPP_CLIENT) return isBnppClient;
    return false;
  };

  return {
    user,
    status,
    // Status booleans
    isLoading: status === USER_STATUS.PENDING,
    isSuccess: status === USER_STATUS.SUCCESS,
    isError: status === USER_STATUS.FAIL,
    isIdle: status === USER_STATUS.IDLE,
    // User state helpers
    isLoggedIn,
    isBnppClient,
    isExpert,
    isEmailConfirmed,
    hasToken,
    // Access level checker
    hasAccessLevel,
    // User properties shortcuts
    userId: user && user.id,
    userType: user && user.user_type,
    consumerType: user && user.consumer_type,
    consumerStatus: user && user.consumer_status,
    allowedTools: user && user.allowed_tools,
    fullName: user ? `${user.first_name} ${user.last_name}`.trim() : "",
    firstName: user && user.first_name,
    lastName: user && user.last_name,
    email: user && user.email,
    userSlug: user && user.slug,
  };
};
